import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { motion } from 'framer-motion';
import { BookOpen, Mountain, ArrowRight } from 'lucide-react';
const quickFacts = [{
  label: 'Trek Duration',
  value: '12-14 Days'
}, {
  label: 'Max Altitude',
  value: '5,545m (Kala Patthar)'
}, {
  label: 'Difficulty',
  value: 'Challenging'
}, {
  label: 'Best Season',
  value: 'Mar-May, Sep-Nov'
}, {
  label: 'Permits Required',
  value: '2 (TIMS + Park Entry)'
}, {
  label: 'Average Cost',
  value: '$1,200-$1,800'
}];
const relatedGuides = [{
  title: 'Complete Packing List for EBC',
  link: '#packing-list'
}, {
  title: 'Altitude Sickness Prevention Guide',
  link: '#altitude-sickness'
}, {
  title: 'Photography Tips for Himalayan Treks',
  link: '#photography-tips'
}, {
  title: 'Nepal Visa & Entry Requirements',
  link: '#nepal-visa'
}, {
  title: 'Choosing the Right Trekking Agency',
  link: '#choosing-agency'
}, {
  title: 'Post-Trek Recovery & Care',
  link: '#post-trek-recovery'
}];
export function EverestTrekGuidePage() {
  return <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-1351">
      <Navbar data-id="element-1352" />

      {/* Hero Section */}
      <div className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden" data-id="element-1353">
        <div className="absolute inset-0 z-0" data-id="element-1354">
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80 z-10" data-id="element-1355" />
          <img src="https://images.unsplash.com/photo-1533130061792-649d45df8c2d?q=80&w=1978&auto=format&fit=crop" alt="Everest Trek" className="w-full h-full object-cover" data-id="element-1356" />
        </div>

        <div className="relative z-20 text-center px-4 max-w-4xl mx-auto mt-16" data-id="element-1357">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="flex justify-center mb-6" data-id="element-1358">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center" data-id="element-1359">
              <BookOpen className="text-white" size={32} data-id="element-1360" />
            </div>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-4xl md:text-6xl font-bold text-white mb-4" data-id="element-1361">
            Everest Trek Guide
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="text-xl text-gray-200 max-w-2xl mx-auto" data-id="element-1362">
            Your complete resource for planning and completing the trek to
            Everest Base Camp
          </motion.p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-1363">
        {/* Quick Facts */}
        <section className="mb-20" data-id="element-1364">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-gradient-to-r from-[#0066FF] to-[#06B6D4] rounded-2xl p-8 md:p-12" data-id="element-1365">
            <h2 className="text-3xl font-bold text-white mb-8 text-center" data-id="element-1366">
              Quick Facts
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-id="element-1367">
              {quickFacts.map((fact, index) => <motion.div key={index} initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="text-center" data-id="element-1368">
                  <p className="text-blue-100 text-sm mb-2" data-id="element-1369">{fact.label}</p>
                  <p className="text-white text-lg font-bold" data-id="element-1370">{fact.value}</p>
                </motion.div>)}
            </div>
          </motion.div>
        </section>

        {/* Overview */}
        <section className="mb-20" data-id="element-1371">
          <div className="max-w-4xl mx-auto" data-id="element-1372">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-1373">
              <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center" data-id="element-1374">
                Overview
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 space-y-4" data-id="element-1375">
                <p data-id="element-1376">
                  The Everest Base Camp trek is one of the most iconic
                  adventures on Earth. This comprehensive guide will help you
                  prepare for every aspect of your journey to the foot of the
                  world's highest mountain.
                </p>
                <p data-id="element-1377">
                  Standing at 5,364 meters (17,598 feet), Everest Base Camp is
                  the staging point for mountaineers attempting to summit Mount
                  Everest. The trek takes you through the heart of the Khumbu
                  region, home to the legendary Sherpa people, ancient Buddhist
                  monasteries, and some of the most spectacular mountain scenery
                  on the planet.
                </p>
                <p data-id="element-1378">
                  This journey is challenging but achievable for anyone with
                  good physical fitness and proper preparation. You don't need
                  technical climbing skills, but you do need determination, the
                  right gear, and a solid understanding of high-altitude
                  trekking. This guide covers everything from planning and
                  permits to acclimatization strategies and what to pack.
                </p>
                <p data-id="element-1379">
                  Whether you're a first-time trekker or an experienced hiker,
                  this guide will ensure you're fully prepared for the adventure
                  of a lifetime. Read through each section carefully, and don't
                  hesitate to reach out to our expert team with any questions.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Related Guides */}
        <section className="mb-20 bg-gray-50 rounded-2xl p-8 md:p-12" data-id="element-1380">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-1381">
            Related Guides & Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1382">
            {relatedGuides.map((guide, index) => <motion.a key={index} href={guide.link} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="group bg-white p-6 rounded-xl border border-gray-200 hover:border-[#0066FF] hover:shadow-lg transition-all" data-id="element-1383">
                <div className="flex items-center justify-between" data-id="element-1384">
                  <h4 className="text-lg font-semibold text-gray-900 group-hover:text-[#0066FF] transition-colors" data-id="element-1385">
                    {guide.title}
                  </h4>
                  <ArrowRight size={20} className="text-gray-400 group-hover:text-[#0066FF] group-hover:translate-x-1 transition-all" data-id="element-1386" />
                </div>
              </motion.a>)}
          </div>
        </section>

        {/* Expert Tip */}
        <section className="mb-20" data-id="element-1387">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-gradient-to-br from-blue-50 to-cyan-50 border-l-4 border-[#0066FF] rounded-r-2xl p-8" data-id="element-1388">
            <div className="flex items-start gap-4" data-id="element-1389">
              <div className="w-12 h-12 bg-[#0066FF] rounded-full flex items-center justify-center shrink-0" data-id="element-1390">
                <Mountain className="text-white" size={24} data-id="element-1391" />
              </div>
              <div data-id="element-1392">
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1393">
                  Expert Tip from Our Lead Guide
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1394">
                  "The most common mistake I see is trekkers rushing the
                  acclimatization process. Your body needs time to adjust to
                  high altitude. Take the scheduled rest days seriously, stay
                  hydrated, and listen to your body. It's not a race—the
                  mountain will still be there tomorrow. Those who take their
                  time enjoy the trek more and have much higher success rates."
                </p>
                <p className="text-gray-900 font-semibold" data-id="element-1395">
                  — Pemba Sherpa, Lead Guide (15+ years experience)
                </p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-[#0066FF] to-[#06B6D4] rounded-2xl p-12 text-center text-white" data-id="element-1396">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1397">
            Ready to Trek to Everest Base Camp?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto" data-id="element-1398">
            Now that you have the complete guide, let our expert team help you
            plan your adventure. We'll handle all the logistics so you can focus
            on the experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1399">
            <Button variant="primary" size="lg" className="bg-white text-[#0066FF] hover:bg-gray-100 border-none" data-id="element-1400">
              Book Your Trek
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-[#0066FF]" data-id="element-1401">
              Contact Our Team
            </Button>
          </div>
        </section>
      </main>

      <Footer data-id="element-1402" />
    </div>;
}